/** Health Score — versione "pesata per KPI" (rev. 2026-05-12).
 *
 * Mirror dell'algoritmo backend `services/health_score.py`. Mantenere
 * SEMPRE allineati: stesso set di KPI primari/secondari, stessi pesi
 * importanza, stesse soglie band 60/70/75 + tie-breaker zona 70..75.
 *
 * Algoritmo:
 *  - Per ogni KPI: contributo grezzo = SEMAPHORE_WEIGHT[sem] (100/80/45).
 *  - Importanza KPI (peso nella media):
 *      * Primari (peso 2): total_sales, avg_ticket, hourly_revenue, occupancy_pct
 *      * Secondari (peso 1): services_count, service_revenue, products_count,
 *        product_revenue, new_client_pct, reviews_count
 *  - Esclusi: available_hours, returning_client_rate.
 *  - Score = somma(peso × contributo) / somma(peso), arrotondato a int 0..100.
 *
 * Band:
 *  - score < 60 → red
 *  - 60 ≤ score < 70 → yellow
 *  - 70 ≤ score ≤ 75 → green se green_count > red_count, altrimenti yellow
 *  - score > 75 → green
 */
import { semaphoreVsBaseline } from './semaphore';

/** Configurazione mutabile a runtime. Inizializzata con i default storici
 *  (verde 100 / giallo 80 / rosso 45, primario 2 / secondario 1, band 60/70/75).
 *  Il bootstrap di `App.js` chiama `setHealthScoreConfig(api_payload)` dopo
 *  il login per allineare al valore persistito in DB. Resta mutabile finché
 *  l'utente modifica i parametri dalle Impostazioni — in quel caso il
 *  componente Settings ri-chiama `setHealthScoreConfig` dopo il save. */
let _CONFIG = {
  semaphoreWeights: { green: 100, yellow: 80, red: 45 },
  bandThresholds: { redBelow: 60, yellowBelow: 70, greenAbove: 75 },
  kpiImportance: { primary: 2, secondary: 1 },
};

/** Set the runtime config from the API payload. Idempotent and safe to call
 *  any number of times. Missing keys retain their previous value. */
export function setHealthScoreConfig(apiPayload) {
  if (!apiPayload || typeof apiPayload !== 'object') return;
  const sw = apiPayload.semaphore_weights;
  if (sw && typeof sw === 'object') {
    _CONFIG.semaphoreWeights = {
      green: Number(sw.green ?? _CONFIG.semaphoreWeights.green),
      yellow: Number(sw.yellow ?? _CONFIG.semaphoreWeights.yellow),
      red: Number(sw.red ?? _CONFIG.semaphoreWeights.red),
    };
  }
  const bt = apiPayload.band_thresholds;
  if (bt && typeof bt === 'object') {
    _CONFIG.bandThresholds = {
      redBelow: Number(bt.red_below ?? _CONFIG.bandThresholds.redBelow),
      yellowBelow: Number(bt.yellow_below ?? _CONFIG.bandThresholds.yellowBelow),
      greenAbove: Number(bt.green_above ?? _CONFIG.bandThresholds.greenAbove),
    };
  }
  const ki = apiPayload.kpi_importance;
  if (ki && typeof ki === 'object') {
    _CONFIG.kpiImportance = {
      primary: Number(ki.primary ?? _CONFIG.kpiImportance.primary),
      secondary: Number(ki.secondary ?? _CONFIG.kpiImportance.secondary),
    };
  }
}

const PRIMARY_KPIS = new Set(['total_sales', 'avg_ticket', 'hourly_revenue', 'occupancy_pct']);
const SECONDARY_KPIS = new Set([
  'services_count', 'service_revenue', 'products_count',
  'product_revenue', 'new_client_pct', 'reviews_count',
]);
const EXCLUDED_KPIS = new Set(['available_hours', 'returning_client_rate']);

/** KPI keys whose semaphore is INVERTED (lower actual = better). */
const INVERTED_KPIS = new Set(['available_hours']);

function kpiImportance(key) {
  if (EXCLUDED_KPIS.has(key)) return null;
  if (PRIMARY_KPIS.has(key)) return _CONFIG.kpiImportance.primary;
  if (SECONDARY_KPIS.has(key)) return _CONFIG.kpiImportance.secondary;
  return null;
}

function bandFrom(score, counts) {
  const { redBelow, yellowBelow, greenAbove } = _CONFIG.bandThresholds;
  if (score < redBelow) return 'red';
  if (score < yellowBelow) return 'yellow';
  if (score <= greenAbove) {
    return (counts.green || 0) > (counts.red || 0) ? 'green' : 'yellow';
  }
  return 'green';
}

function emptyHealth() {
  return { score: null, band: 'neutral',
           counts: { green: 0, yellow: 0, red: 0 }, sample: 0 };
}

/** Compute health from an array of KPI rows (Store Dashboard shape).
 *  Each row must expose `key` and `sem_budget`. */
export function healthFromKpiArray(kpis) {
  const sw = _CONFIG.semaphoreWeights;
  const counts = { green: 0, yellow: 0, red: 0 };
  let total = 0;
  let totalWeight = 0;
  let n = 0;
  for (const k of kpis || []) {
    const w = kpiImportance(k.key);
    if (w === null) continue;
    const sem = k.sem_budget;
    if (!sem || sw[sem] === undefined) continue;
    counts[sem] += 1;
    total += sw[sem] * w;
    totalWeight += w;
    n += 1;
  }
  if (n === 0 || totalWeight === 0) return emptyHealth();
  const score = Math.round(total / totalWeight);
  return { score, band: bandFrom(score, counts), counts, sample: n };
}

/** Compute health from the map shape (`{kpi_key: {actual, budget}, ...}`). */
export function healthFromKpiMap(kpiMap) {
  if (!kpiMap || typeof kpiMap !== 'object') return emptyHealth();
  const sw = _CONFIG.semaphoreWeights;
  const counts = { green: 0, yellow: 0, red: 0 };
  let total = 0;
  let totalWeight = 0;
  let n = 0;
  for (const [key, v] of Object.entries(kpiMap)) {
    const w = kpiImportance(key);
    if (w === null) continue;
    if (!v || v.actual == null || v.budget == null || v.budget === 0) continue;
    let sem = semaphoreVsBaseline(v.actual, v.budget);
    if (INVERTED_KPIS.has(key) && sem) {
      sem = sem === 'green' ? 'red' : sem === 'red' ? 'green' : 'yellow';
    }
    if (!sem || sw[sem] === undefined) continue;
    counts[sem] += 1;
    total += sw[sem] * w;
    totalWeight += w;
    n += 1;
  }
  if (n === 0 || totalWeight === 0) return emptyHealth();
  const score = Math.round(total / totalWeight);
  return { score, band: bandFrom(score, counts), counts, sample: n };
}

/** Tailwind class for the score badge background. */
export const HEALTH_BAND_BG = {
  green: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  yellow: 'bg-amber-50 text-amber-700 border-amber-200',
  red: 'bg-red-50 text-red-700 border-red-200',
  neutral: 'bg-slate-50 text-slate-500 border-slate-200',
};

/** Tailwind stroke class for the SVG score ring. */
export const HEALTH_BAND_RING = {
  green: 'stroke-emerald-500',
  yellow: 'stroke-amber-500',
  red: 'stroke-red-500',
  neutral: 'stroke-slate-400',
};
